import { Dispatch, FC, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Scheme } from '@pages/schemes/types';
import DeployFirstStep from './DeployFirstStep';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectDeployedSchemesForEntity,
    selectDeployEntityResponse,
    selectDeployEntityStatus,
} from '@pages/stands/store/selectors';
import {
    deployEntityRequest,
    deployEntityReset,
    getDeployedSchemesForEntityRequest,
    getDeployedSchemesForEntityReset,
} from '@pages/stands/store';
import DeploySecondStep from './DeploySecondStep';
import DeployThirdStep from './DeployThirdStep';
import dayjs from 'dayjs';
import { StandEntityType } from '@pages/stands/types';
import { getDayTime, getStandLinkBySchemeId } from '@shared/utils/utilsOld';
import { AccessEnum, RequestStatusEnum, SelectedFilter } from '@shared/types';
import { useNavigate } from 'react-router-dom';
import { DateTimeValidationError } from '@mui/x-date-pickers';
import { UI_FUNCTIONAL_TESTING_PATH, UI_TEST_RUN_PATH } from '@shared/constants/pathsConstants';
import { getDevicesRequiredForSchemeRequest } from '@pages/schemes/store';
import { selectDevicesRequiredForScheme } from '@pages/schemes/store/selectors';
import { isTruthy } from '@shared/types/helpers';
import { DevicesResponse } from '@pages/booking/types';
import SimpleAlert from '@shared/components/modals/SimpleAlert';
import { selectSelectedBookings } from '@pages/booking/store/selectors';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { useBookingChunk } from '@pages/booking/hooks/useBookingChunk';
import { useSchemesChunk } from '@pages/schemes/hooks/useSchemesChunk';
import DeployBookingStep from '@pages/stands/components/DeployModal/DeployBookingStep';
import {
    DEFAULT_SHOULD_ACTIVATE_LICENSE,
    DEFAULT_SHOULD_ENABLE_ROOT_ACCESS,
    DEFAULT_SHOULD_REGISTER_NODE,
} from '@pages/stands/constants';
import useMultiStepDialog from '@shared/hooks/useMultiStepDialog';
import MultiStepDialogStepper from '@shared/components/others/MultiStepDialogStepper/MultiStepDialogStepper';
import MultiStepDialogActions from '@shared/components/others/MultiStepDialogActions/MultiStepDialogActions';
import ModalSuccessContainer from '@shared/components/modals/ModalSuccessContainer';

export type FirstStepError = 'branch' | 'build' | 'architecture' | null;

type AdditionalComponents = 'checkbox';

export interface DeployRunModalProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    entityType: StandEntityType;
    entity: { pk: number; name?: string; title?: string; is_handtest?: boolean };
    showStepper?: boolean;
    showFirstStep?: boolean;
    showSecondStep?: boolean;
    additionalComponents?: Record<AdditionalComponents, ReactNode>;
    params?: Record<string, unknown>;
    onSuccess?: () => void;
    testType?: 'hand' | 'auto' | 'mixed';
}

const defaultDeployForm: SelectedFilter = {
    branch: '',
    build: '',
    build_details: { architecture: '' },
    is_public: false,
};

const DeployRunModal: FC<DeployRunModalProps> = (props) => (
    <ComponentWithChunks
        hooksArray={[useBookingChunk, useSchemesChunk]}
        featuresArray={['booking', 'schemes']}
        Component={DeployRunModalComponent}
        {...props}
    />
);

const DeployRunModalComponent: FC<DeployRunModalProps> = ({
    open,
    setOpen,
    entityType,
    entity,
    showStepper = true,
    showFirstStep = true,
    showSecondStep = true,
    additionalComponents,
    params,
    onSuccess,
    testType,
}) => {
    const { t } = useTranslation();
    const deployEntityStatus = useSelector(selectDeployEntityStatus);
    const [shouldRegisterNode, setShouldRegisterNode] = useState(DEFAULT_SHOULD_REGISTER_NODE);
    const [shouldActivateLicense, setShouldActivateLicense] = useState(DEFAULT_SHOULD_ACTIVATE_LICENSE);
    const [shouldEnableRootAccess, setShouldEnableRootAccess] = useState(DEFAULT_SHOULD_ENABLE_ROOT_ACCESS);
    const [standExpireDateError, setStandExpireDateError] = useState<DateTimeValidationError>(null);
    const [comment, setComment] = useState('');
    const deployedSchemesForEntity = useSelector(selectDeployedSchemesForEntity);
    const [schemesToDeploy, setSchemesToDeploy] = useState<Scheme[]>([]);
    const dispatch = useDispatch();
    const initialStandExpireDateTime = useMemo(() => dayjs(getDayTime(1, 60)), []);
    const [standExpireDateTime, setStandExpireDateTime] = useState(initialStandExpireDateTime);
    const [access, setAccess] = useState<AccessEnum>(AccessEnum.Locked);
    const [navigateToStandOrTestrun, setNavigateToStandOrTestrun] = useState(true);
    const navigate = useNavigate();
    const deployEntityResponse = useSelector(selectDeployEntityResponse);
    const devicesRequiredForScheme: DevicesResponse = useSelector(selectDevicesRequiredForScheme);
    const needBookingStep = Boolean(devicesRequiredForScheme?.dut?.length);
    const [validationError, setValidationError] = useState('');
    const [openValidationAlert, setOpenValidationAlert] = useState(false);
    const bookingSelected = useSelector(selectSelectedBookings);
    const isHandtest = testType === 'hand' || entity?.is_handtest === true;
    const isAuto = testType === 'auto' || entity?.is_handtest === false;
    const isMixed = testType === 'mixed';
    const isCheckboxActive = Boolean(params?.skip_deploy_for_hand_tests);
    const shouldShowDateTimePicker =
        showSecondStep || (entityType === 'testplan' && !params?.skip_deploy_for_hand_tests);
    const shouldSkipDeployForHandTests = isHandtest && isCheckboxActive;

    const visibilityLogic = useMemo(() => {
        const isSkipAllSteps = isHandtest && isCheckboxActive;
        const shouldShowFirstStep = !isSkipAllSteps && showFirstStep;
        const shouldShowSecondStep = !isSkipAllSteps && showSecondStep;
        const shouldShowThirdStep = !isSkipAllSteps;
        const shouldShowBookingStep = !isSkipAllSteps && needBookingStep;
        const stepCount = [
            shouldShowFirstStep,
            shouldShowSecondStep,
            shouldShowThirdStep,
            shouldShowBookingStep,
        ].filter(Boolean).length;

        return {
            showStepperComponent: !isSkipAllSteps && showStepper && stepCount > 1,
            shouldShowFirstStep,
            shouldShowSecondStep,
            shouldShowThirdStep,
            shouldShowBookingStep,
            effectiveStepCount: isSkipAllSteps ? 1 : stepCount,
        };
    }, [isHandtest, isCheckboxActive, showStepper, showFirstStep, showSecondStep, needBookingStep]);

    const {
        activeStepIndex,
        setActiveStepIndex,
        isFirstStep,
        isLastStep,
        handleNext,
        handleBack,
        handleStep,
        selectedFilter,
        setSelectedFilter,
        handleFilterChange,
        error,
    } = useMultiStepDialog({
        totalSteps: visibilityLogic.effectiveStepCount,
    });

    const isDeployButtonDisabled =
        isLastStep && !shouldSkipDeployForHandTests && (!selectedFilter.build || Boolean(standExpireDateError));

    const steps = useMemo(() => {
        if (isHandtest && isCheckboxActive) {
            return [];
        }

        const allSteps = [
            visibilityLogic.shouldShowFirstStep ? t('deploy_options') : null,
            visibilityLogic.shouldShowSecondStep ? t('scheme_configuration') : null,
            visibilityLogic.shouldShowThirdStep ? t('stand_options') : null,
            visibilityLogic.shouldShowBookingStep ? t('booking') : null,
        ];

        return allSteps.filter(isTruthy).map((item) => item.toUpperCase());
    }, [
        isHandtest,
        isCheckboxActive,
        visibilityLogic.shouldShowFirstStep,
        visibilityLogic.shouldShowSecondStep,
        visibilityLogic.shouldShowThirdStep,
        visibilityLogic.shouldShowBookingStep,
        t,
    ]);

    const stepIndices = useMemo(() => {
        let currentIndex = 0;
        const indices = {
            first: -1,
            second: -1,
            third: -1,
            booking: -1,
        };

        if (visibilityLogic.shouldShowFirstStep) {
            indices.first = currentIndex;
            currentIndex++;
        }

        if (visibilityLogic.shouldShowSecondStep) {
            indices.second = currentIndex;
            currentIndex++;
        }

        if (visibilityLogic.shouldShowThirdStep) {
            indices.third = currentIndex;
            currentIndex++;
        }

        if (visibilityLogic.shouldShowBookingStep) {
            indices.booking = currentIndex;
        }

        return indices;
    }, [
        visibilityLogic.shouldShowFirstStep,
        visibilityLogic.shouldShowSecondStep,
        visibilityLogic.shouldShowThirdStep,
        visibilityLogic.shouldShowBookingStep,
    ]);

    useEffect(() => {
        if (Object.keys(bookingSelected).length > 0) {
            setValidationError('');
            setOpenValidationAlert(false);
        }
    }, [bookingSelected]);

    const handleCloseModal = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const findDevicesWithoutBookings = () => {
        return devicesRequiredForScheme
            ? devicesRequiredForScheme.dut.filter((device) => !bookingSelected[device.pk])
            : [];
    };

    const handleDeployRun = () => {
        const devicesWithoutBookings = findDevicesWithoutBookings();
        const allBookingsAreValid = needBookingStep ? !devicesWithoutBookings.length : true;

        if (!allBookingsAreValid) {
            setOpenValidationAlert(true);
            setValidationError(
                `${t('deploy_booking_devices_missing_message')}: 
                ${devicesWithoutBookings.map((device) => device.name).join(', ')}`,
            );
            return;
        } else {
            setOpenValidationAlert(false);
            setValidationError('');
        }

        let customJsonConfig;

        if (schemesToDeploy.length) {
            if (entityType === 'scheme' || entityType === 'testcase') {
                customJsonConfig = schemesToDeploy[0].json_config;
            }
            // else if (entityType === 'testplan') {
            //     customJsonConfig = schemesToDeploy.map((scheme: Scheme) => ({
            //         pk: scheme.pk,
            //         json_config: scheme.json_config,
            //     }));
            // }
        }

        const bookingsIds = Object.values(bookingSelected).map((booking) => booking.pk);

        dispatch(
            deployEntityRequest({
                type: entityType,
                id: entity.pk,
                params: {
                    bookingsIds: bookingsIds.length ? bookingsIds : undefined,
                    ...(shouldShowDateTimePicker
                        ? {
                              branchName: selectedFilter.branch,
                              buildNumber: selectedFilter.build,
                              isPublic: selectedFilter.is_public,
                              architecture: selectedFilter.build_details.architecture,
                              expires_at: standExpireDateTime.toISOString(),
                              should_register_ug_nodes: shouldRegisterNode,
                              should_activate_license: shouldActivateLicense,
                              should_enable_root_ssh: shouldEnableRootAccess,
                              comment: comment,
                          }
                        : {}),
                    ...(entityType === 'testplan'
                        ? {
                              jsonConfig: customJsonConfig,
                          }
                        : {}),
                    access,
                    ...params,
                },
            }),
        );
        if (onSuccess) {
            onSuccess();
        }
    };

    useEffect(() => {
        return () => {
            dispatch(getDeployedSchemesForEntityReset());
            dispatch(deployEntityReset());
        };
    }, [dispatch]);

    // Reset active tab to first on every modal open
    useEffect(() => {
        setActiveStepIndex(0);
        setSchemesToDeploy([]);
        setAccess(AccessEnum.Locked);
        setValidationError('');
    }, [open, setActiveStepIndex]);

    const getAccessFromSchemes = useCallback(() => {
        for (let i = 0; i < deployedSchemesForEntity.length; i += 1) {
            if (deployedSchemesForEntity[i].access === AccessEnum.Locked) {
                return AccessEnum.Locked;
            }
            if (deployedSchemesForEntity[i].access === AccessEnum.Private) {
                return AccessEnum.Private;
            }
        }
        return AccessEnum.Public;
    }, [deployedSchemesForEntity]);

    useEffect(() => {
        setSchemesToDeploy(deployedSchemesForEntity);
        setAccess(getAccessFromSchemes());
    }, [deployedSchemesForEntity, getAccessFromSchemes]);

    useEffect(() => {
        if (shouldSkipDeployForHandTests) {
            setActiveStepIndex(0);
        }
    }, [shouldSkipDeployForHandTests, setActiveStepIndex]);

    const resetFieldsToInitial = useCallback(() => {
        setSelectedFilter(defaultDeployForm);
        setShouldRegisterNode(DEFAULT_SHOULD_REGISTER_NODE);
        setShouldActivateLicense(DEFAULT_SHOULD_ACTIVATE_LICENSE);
        setShouldEnableRootAccess(DEFAULT_SHOULD_ENABLE_ROOT_ACCESS);
        setStandExpireDateTime(initialStandExpireDateTime);
        setNavigateToStandOrTestrun(true);
        setComment('');
    }, [initialStandExpireDateTime, setSelectedFilter]);

    useEffect(() => {
        if (!entity?.pk) {
            return;
        }

        if (open) {
            if (entityType !== 'testplan') {
                dispatch(getDeployedSchemesForEntityRequest({ entity: entityType, id: entity.pk }));
            }
            if (entityType === 'scheme') {
                dispatch(getDevicesRequiredForSchemeRequest(entity.pk));
            }
        } else {
            resetFieldsToInitial();
        }
    }, [dispatch, entity?.pk, entityType, open, resetFieldsToInitial]);

    const getLinkForNavigateAfterDeploy = useCallback(
        (pk: number, entityType: StandEntityType) => {
            switch (entityType) {
                case 'scheme':
                    return navigate(getStandLinkBySchemeId(pk, false, 'pk'));
                case 'testcase':
                case 'testplan': {
                    return navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_RUN_PATH}/${pk}`);
                }
            }
        },
        [navigate],
    );

    useEffect(() => {
        if (deployEntityStatus === RequestStatusEnum.Success) {
            if (navigateToStandOrTestrun) {
                deployEntityResponse?.pk && getLinkForNavigateAfterDeploy(deployEntityResponse.pk, entityType);
            } else {
                dispatch(deployEntityReset());
            }

            handleCloseModal();
        }
    }, [
        deployEntityResponse?.pk,
        deployEntityStatus,
        dispatch,
        entityType,
        getLinkForNavigateAfterDeploy,
        handleCloseModal,
        navigateToStandOrTestrun,
        setOpen,
    ]);

    const entityNameForTitle = useMemo(() => {
        switch (entityType) {
            case 'scheme':
                return t('scheme_v');
            case 'testplan':
                return t('test_plan');
            case 'testcase':
                return t('test_case');
        }
    }, [entityType, t]);

    return (
        <>
            <Dialog
                slotProps={{
                    paper: {
                        sx: { minHeight: isCheckboxActive && !isMixed ? 0 : 600, minWidth: 900 },
                    },
                }}
                maxWidth="xl"
                open={open}
            >
                <DialogTitle variant="h4" sx={{ fontSize: 14, fontWeight: 600 }}>
                    {`${t('deploy')} ${entityNameForTitle} - ${entity?.name || entity?.title}`.toUpperCase()}
                </DialogTitle>
                <DialogContent sx={{ pb: isCheckboxActive && !isMixed ? 0 : 3 }}>
                    {!isAuto && additionalComponents?.checkbox}
                    {visibilityLogic.showStepperComponent && (
                        <MultiStepDialogStepper
                            steps={steps}
                            activeStepIndex={activeStepIndex}
                            onStepClick={handleStep}
                            stepperProps={{
                                sx: { pt: 2, pb: 2, fontSize: 14, fontWeight: 600 },
                            }}
                        />
                    )}
                    <Box sx={{ overflowY: 'auto' }}>
                        {visibilityLogic.shouldShowFirstStep && stepIndices.first !== -1 && (
                            <DeployFirstStep
                                isCurrentStep={activeStepIndex === stepIndices.first}
                                selectedFilter={selectedFilter}
                                setSelectedFilter={handleFilterChange}
                                mainModalOpen={open}
                                error={error}
                                shouldRegisterNode={shouldRegisterNode}
                                setShouldRegisterNode={setShouldRegisterNode}
                                shouldActivateLicense={shouldActivateLicense}
                                setShouldActivateLicense={setShouldActivateLicense}
                                showProductConfiguration={!Boolean(params?.skip_deploy_for_hand_tests)}
                                shouldEnableRootAccess={shouldEnableRootAccess}
                                setShouldEnableRootAccess={setShouldEnableRootAccess}
                            />
                        )}
                        {visibilityLogic.shouldShowSecondStep && stepIndices.second !== -1 && (
                            <DeploySecondStep
                                isCurrentStep={activeStepIndex === stepIndices.second}
                                schemesToDeploy={schemesToDeploy}
                                setSchemesToDeploy={setSchemesToDeploy}
                            />
                        )}
                        {visibilityLogic.shouldShowThirdStep && stepIndices.third !== -1 && (
                            <DeployThirdStep
                                isCurrentStep={activeStepIndex === stepIndices.third}
                                standExpireDateTime={standExpireDateTime}
                                setStandExpireDateTime={setStandExpireDateTime}
                                setStandExpireDateError={setStandExpireDateError}
                                access={access}
                                setAccess={setAccess}
                                navigateToStandOrTestrun={navigateToStandOrTestrun}
                                setNavigateToStandOrTestrun={setNavigateToStandOrTestrun}
                                entityType={entityType}
                                comment={comment}
                                setComment={setComment}
                                showDateTimePicker={shouldShowDateTimePicker}
                            />
                        )}
                        {visibilityLogic.shouldShowBookingStep && stepIndices.booking !== -1 && (
                            <DeployBookingStep
                                isCurrentStep={activeStepIndex === stepIndices.booking}
                                standExpireDateTime={standExpireDateTime}
                            />
                        )}
                    </Box>
                </DialogContent>
                <MultiStepDialogActions
                    isFirstStep={isFirstStep}
                    isLastStep={isLastStep}
                    onNext={isLastStep ? handleDeployRun : handleNext}
                    onBack={handleBack}
                    onCancel={handleCloseModal}
                    nextButtonConfig={{
                        text: isLastStep ? t('deploy') : undefined,
                        disabled: isDeployButtonDisabled,
                        loading: deployEntityStatus === RequestStatusEnum.Loading,
                        color: isLastStep && validationError ? 'error' : 'primary',
                    }}
                    useDialogActions
                    beforeButtons={
                        openValidationAlert && (
                            <SimpleAlert
                                severity="error"
                                message={validationError}
                                onClose={() => setOpenValidationAlert(false)}
                                autoHideDuration={4000}
                            />
                        )
                    }
                />
            </Dialog>
            {deployEntityStatus === RequestStatusEnum.Success && (
                <ModalSuccessContainer
                    object={t(entityType)}
                    resetActionCallback={deployEntityReset}
                    action={t('running').toLowerCase()}
                />
            )}
        </>
    );
};

export default DeployRunModal;
